package com.evoapps.evoshift.ui.theme

import androidx.compose.animation.AnimatedContentTransitionScope
import androidx.compose.animation.AnimatedVisibilityScope
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.ExperimentalSharedTransitionApi
import androidx.compose.animation.SharedTransitionScope
import androidx.compose.animation.core.CubicBezierEasing
import androidx.compose.animation.core.Easing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.navigation.NavBackStackEntry
import com.evoapps.evoshift.Route
import com.evoapps.evoshift.navigation.AppTab
import com.evoapps.evoshift.navigation.routeClass
import kotlin.reflect.KClass

/**
 * The app's motion (SH-103), one place for every screen change:
 *
 * * a screen opened on top ([NavMotion.PUSH]) and back ([NavMotion.POP]): Material's shared
 *   axis X, the screens slide a little ([SLIDE_FRACTION]) and fade through each other;
 * * another section of the bottom bar or rail ([NavMotion.TAB]): fade through, no slide (the
 *   sections sit side by side, not on top of each other);
 * * the whole app replaced (signing in or out, the first run) or one screen replacing another
 *   of its kind (a month for a month): fade through as well;
 * * reduced motion asked for by the system ([prefersReducedMotion]): a short plain fade;
 * * on iOS ([StackMotion.EDGE_SWIPE], SH-198) a screen opened on top covers the one below
 *   ([NavMotion.COVER]) and back uncovers it ([NavMotion.UNCOVER]): the back swipe drags the
 *   screen off by the finger, so the previous screen must be there, whole, underneath. Shared
 *   axis X fades both out halfway, and mid-swipe that showed nothing (black) between them.
 *
 * The top bar takes no part: it stays where it is and only its contents change
 * ([sharedTopBar]). Dialogs and sheets keep their own motion.
 */
object Motion {
    /** A screen opened on top or closed (Material: 300 ms for full-screen transitions). */
    const val NAV_DURATION_MS = 300

    /** A section change: shorter, nothing travels. */
    const val TAB_DURATION_MS = 250

    /** Reduced motion: a quick fade only. */
    const val REDUCED_DURATION_MS = 120

    /** How far the screens travel in shared axis X, of the width (Material: 30 dp of a phone). */
    const val SLIDE_FRACTION = 0.12f

    /** Where the outgoing screen has faded out and the incoming one starts (fade through). */
    const val FADE_THROUGH_SPLIT = 0.35f

    /** Fade through's incoming screen grows from this scale. */
    const val FADE_THROUGH_SCALE = 0.94f

    /**
     * SH-198: how far the screen below shifts while it is covered or uncovered on iOS (UIKit's
     * navigation parallax, about a third of the width).
     */
    const val EDGE_SWIPE_PARALLAX = 0.3f

    /** Material 3 emphasized easing (standard transitions). */
    val Emphasized: Easing = CubicBezierEasing(0.2f, 0f, 0f, 1f)

    /** Material 3 emphasized decelerate (elements entering). */
    val EmphasizedDecelerate: Easing = CubicBezierEasing(0.05f, 0.7f, 0.1f, 1f)

    /** Material 3 emphasized accelerate (elements leaving). */
    val EmphasizedAccelerate: Easing = CubicBezierEasing(0.3f, 0f, 0.8f, 0.15f)

    /** SH-158: small changes inside a screen (a label, a figure, a row fading). */
    const val SHORT_MS = 150

    /** SH-158: a part of a screen appearing, resizing or leaving. */
    const val MEDIUM_MS = 250

    /** SH-158: how far a big primary target sinks while pressed (press only, never on hover; SH-130). */
    const val PRESS_SCALE = 0.96f
}

/** How one screen change animates. */
enum class NavMotion { PUSH, POP, TAB, FADE_THROUGH, REDUCED, COVER, UNCOVER }

/** How the platform moves through a stack of screens (SH-198). */
enum class StackMotion {
    /** Material's shared axis X (Android, the web, desktop); Android's predictive back seeks it. */
    SHARED_AXIS,

    /**
     * iOS: screens slide over each other from the edge ([NavMotion.COVER] / [NavMotion.UNCOVER]),
     * every back included (the edge swipe seeks the back transition by the finger, from a section
     * too), so the previous screen is always drawn whole under the one leaving.
     */
    EDGE_SWIPE
}

/** This platform's [StackMotion]. */
expect val platformStackMotion: StackMotion

/** The [StackMotion] the NavHost uses: the platform's; a test can provide the other. */
val LocalStackMotion = staticCompositionLocalOf { platformStackMotion }

/**
 * The motion from [from] (in section [fromTab]) to [to] (in [toTab]); [isPop] when going back.
 * Unknown sections (null) count as the same section.
 */
fun navMotion(
    from: KClass<out Route>?,
    to: KClass<out Route>?,
    fromTab: AppTab?,
    toTab: AppTab?,
    isPop: Boolean,
    reducedMotion: Boolean,
    stackMotion: StackMotion = StackMotion.SHARED_AXIS
): NavMotion {
    val edgeSwipe = stackMotion == StackMotion.EDGE_SWIPE
    return when {
        reducedMotion -> NavMotion.REDUCED
        from == null || to == null || from in appReplacingRoutes || to in appReplacingRoutes -> NavMotion.FADE_THROUGH
        // iOS: back from a section too (to Home) is an edge swipe, so the screen below shows.
        edgeSwipe && isPop -> NavMotion.UNCOVER
        fromTab != null && toTab != null && fromTab != toTab -> NavMotion.TAB
        // A month for a month (the top bar's arrows): the same place, other contents.
        from == to && !isPop -> NavMotion.FADE_THROUGH
        isPop -> NavMotion.POP
        edgeSwipe -> NavMotion.COVER
        else -> NavMotion.PUSH
    }
}

/** Screens that replace the whole app rather than open on top of it. */
private val appReplacingRoutes: Set<KClass<out Route>> = setOf(Route.Login::class, Route.Onboarding::class)

/** The incoming screen's transition for [motion]. */
fun AnimatedContentTransitionScope<*>.enterFor(motion: NavMotion): EnterTransition = when (motion) {
    NavMotion.PUSH, NavMotion.POP -> {
        val towards = if (motion == NavMotion.PUSH) AnimatedContentTransitionScope.SlideDirection.Start
        else AnimatedContentTransitionScope.SlideDirection.End
        slideIntoContainer(towards, tween(Motion.NAV_DURATION_MS, easing = Motion.Emphasized)) { (it * Motion.SLIDE_FRACTION).toInt() } +
            fadeIn(fadeThroughIn(Motion.NAV_DURATION_MS))
    }
    NavMotion.TAB, NavMotion.FADE_THROUGH ->
        fadeIn(fadeThroughIn(Motion.TAB_DURATION_MS)) +
            scaleIn(fadeThroughIn(Motion.TAB_DURATION_MS, Motion.EmphasizedDecelerate), initialScale = Motion.FADE_THROUGH_SCALE)
    NavMotion.REDUCED -> fadeIn(tween(Motion.REDUCED_DURATION_MS, easing = LinearEasing))
    // Over the screen below, the whole width, opaque all the way.
    NavMotion.COVER -> slideIntoContainer(AnimatedContentTransitionScope.SlideDirection.Start, tween(Motion.NAV_DURATION_MS, easing = Motion.Emphasized))
    // The screen below, whole from the first frame, catching up its parallax; linear, as the
    // back swipe seeks this by the finger's progress (the finger and the screen move together).
    NavMotion.UNCOVER -> slideIntoContainer(AnimatedContentTransitionScope.SlideDirection.End, tween(Motion.NAV_DURATION_MS, easing = LinearEasing)) {
        (it * Motion.EDGE_SWIPE_PARALLAX).toInt()
    }
}

/** The outgoing screen's transition for [motion]. */
fun AnimatedContentTransitionScope<*>.exitFor(motion: NavMotion): ExitTransition = when (motion) {
    NavMotion.PUSH, NavMotion.POP -> {
        val towards = if (motion == NavMotion.PUSH) AnimatedContentTransitionScope.SlideDirection.Start
        else AnimatedContentTransitionScope.SlideDirection.End
        slideOutOfContainer(towards, tween(Motion.NAV_DURATION_MS, easing = Motion.Emphasized)) { (it * Motion.SLIDE_FRACTION).toInt() } +
            fadeOut(fadeThroughOut(Motion.NAV_DURATION_MS))
    }
    NavMotion.TAB, NavMotion.FADE_THROUGH -> fadeOut(fadeThroughOut(Motion.TAB_DURATION_MS))
    NavMotion.REDUCED -> fadeOut(tween(Motion.REDUCED_DURATION_MS, easing = LinearEasing))
    // The covered screen shifts back a little, still drawn whole under the incoming one.
    NavMotion.COVER -> slideOutOfContainer(AnimatedContentTransitionScope.SlideDirection.Start, tween(Motion.NAV_DURATION_MS, easing = Motion.Emphasized)) {
        (it * Motion.EDGE_SWIPE_PARALLAX).toInt()
    }
    // Off to the edge with the finger, the whole width; the NavHost draws it above the one below.
    NavMotion.UNCOVER -> slideOutOfContainer(AnimatedContentTransitionScope.SlideDirection.End, tween(Motion.NAV_DURATION_MS, easing = LinearEasing))
}

/** Fade through: the outgoing screen is gone by the split… */
private fun <T> fadeThroughOut(total: Int) =
    tween<T>((total * Motion.FADE_THROUGH_SPLIT).toInt(), easing = Motion.EmphasizedAccelerate)

/** …and the incoming one takes the rest. */
private fun <T> fadeThroughIn(total: Int, easing: Easing = Motion.EmphasizedDecelerate) =
    tween<T>(total - (total * Motion.FADE_THROUGH_SPLIT).toInt(), delayMillis = (total * Motion.FADE_THROUGH_SPLIT).toInt(), easing = easing)

/**
 * Which section each screen of the back stack belongs to: the last section root below it (Home
 * when none). Remembered for screens already gone, as the one being left has left the stack
 * when its transition is chosen.
 */
class NavEntryTabs {
    private val known = LinkedHashMap<String, AppTab>()

    /** Records the sections of [stack] for [tabs]. */
    fun record(stack: List<NavBackStackEntry>, tabs: List<AppTab>) {
        var section = AppTab.HOME
        stack.forEach { entry ->
            val route = entry.destination.routeClass()
            tabs.firstOrNull { it.rootClass == route }?.let { section = it }
            known.remove(entry.id)
            known[entry.id] = section
        }
        // The newest screens are all that is ever asked about.
        while (known.size > MAX_KNOWN) known.remove(known.keys.first())
    }

    fun tabOf(entry: NavBackStackEntry): AppTab? = known[entry.id]

    private companion object {
        const val MAX_KNOWN = 64
    }
}

/** The motion of a NavHost transition, from its back stack entries. */
fun AnimatedContentTransitionScope<NavBackStackEntry>.navMotion(
    tabs: NavEntryTabs,
    isPop: Boolean,
    reducedMotion: Boolean,
    stackMotion: StackMotion = StackMotion.SHARED_AXIS
): NavMotion = navMotion(
    from = initialState.destination.routeClass(),
    to = targetState.destination.routeClass(),
    fromTab = tabs.tabOf(initialState),
    toTab = tabs.tabOf(targetState),
    isPop = isPop,
    reducedMotion = reducedMotion,
    stackMotion = stackMotion
)

/** Whether the platform asks for less motion (Android: animations off, iOS: Reduce Motion, web: prefers-reduced-motion). */
@Composable
expect fun prefersReducedMotion(): Boolean

/**
 * SH-158: whether the app moves less, read by every animation inside the screens (ActionMotion.kt).
 * Provided once by the shell from [prefersReducedMotion]; a test can provide true around it.
 */
val LocalReducedMotion = staticCompositionLocalOf { false }

/** The layout the screens' shared top bar lives in (App.kt), null outside the NavHost. */
@OptIn(ExperimentalSharedTransitionApi::class)
val LocalSharedTransitionScope = compositionLocalOf<SharedTransitionScope?> { null }

/** The screen's own enter/exit in the NavHost, null outside it. */
val LocalNavAnimatedVisibilityScope = compositionLocalOf<AnimatedVisibilityScope?> { null }

/**
 * Which of a screen's top bars takes part in the shared bounds: the first one composed (the
 * screen's own, from its Scaffold's top bar slot). A bar shown inside the screen, such as a form
 * opened in a wide window's detail pane (SH-105), stays a plain bar. Two visible elements under
 * one shared key in the same scope never settle, so the transition (and Compose) never goes idle.
 */
class SharedTopBarSlot {
    private var holder: Any? = null

    /** Whether [bar] is (now) the screen's shared bar. */
    internal fun claim(bar: Any): Boolean {
        if (holder == null) holder = bar
        return holder === bar
    }

    internal fun release(bar: Any) {
        if (holder === bar) holder = null
    }
}

/** The current screen's [SharedTopBarSlot], null outside the NavHost. */
val LocalSharedTopBarSlot = staticCompositionLocalOf<SharedTopBarSlot?> { null }

private const val TOP_BAR_KEY = "evoshift_top_bar"

/**
 * The top bar stays put while the screens change under it (SH-103): the outgoing and incoming
 * screens' bars share their bounds, so the bar only resizes when their heights differ and its
 * contents cross-fade. The incoming contents are in fully before the outgoing ones start to go,
 * so the navy never thins. Only a screen's first bar takes part ([SharedTopBarSlot]). Outside
 * the NavHost (tests, previews) nothing changes.
 */
@OptIn(ExperimentalSharedTransitionApi::class)
@Composable
fun Modifier.sharedTopBar(): Modifier {
    val shared = LocalSharedTransitionScope.current ?: return this
    val visibility = LocalNavAnimatedVisibilityScope.current ?: return this
    val slot = LocalSharedTopBarSlot.current ?: return this
    val bar = remember { Any() }
    DisposableEffect(slot, bar) { onDispose { slot.release(bar) } }
    if (!slot.claim(bar)) return this
    val split = (Motion.NAV_DURATION_MS * Motion.FADE_THROUGH_SPLIT).toInt()
    return with(shared) {
        this@sharedTopBar.sharedBounds(
            sharedContentState = rememberSharedContentState(TOP_BAR_KEY),
            animatedVisibilityScope = visibility,
            enter = fadeIn(tween(split, easing = LinearEasing)),
            exit = fadeOut(tween(Motion.NAV_DURATION_MS - split, delayMillis = split, easing = LinearEasing)),
            boundsTransform = { _, _ -> tween(Motion.NAV_DURATION_MS, easing = Motion.Emphasized) },
            resizeMode = SharedTransitionScope.ResizeMode.RemeasureToBounds
        )
    }
}
